import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROL_ETIKETLERI } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  LayoutDashboard,
  TrendingUp,
  Wallet,
  Package,
  ArrowLeftRight,
  Gauge,
  Users,
  Settings,
  Menu,
  LogOut,
} from "lucide-react";

export const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["yonetici", "operasyon", "goruntuleyici"], testid: "sidebar-nav-dashboard-link" },
  { path: "/ciro", label: "Ciro", icon: TrendingUp, roles: ["yonetici", "operasyon"], testid: "sidebar-nav-ciro-link" },
  { path: "/gider", label: "Gider & Bütçe", icon: Wallet, roles: ["yonetici", "operasyon"], testid: "sidebar-nav-gider-link" },
  { path: "/maliyet", label: "Maliyet (SMM)", icon: Package, roles: ["yonetici", "operasyon"], testid: "sidebar-nav-maliyet-link" },
  { path: "/nakit-akis", label: "Nakit Akış", icon: ArrowLeftRight, roles: ["yonetici", "goruntuleyici"], testid: "sidebar-nav-nakit-akis-link" },
  { path: "/finansal-analiz", label: "Finansal Analiz", icon: Gauge, roles: ["yonetici", "goruntuleyici"], testid: "sidebar-nav-finansal-analiz-link" },
  { path: "/personel", label: "Personel", icon: Users, roles: ["yonetici"], testid: "sidebar-nav-personel-link" },
  { path: "/ayarlar", label: "Ayarlar", icon: Settings, roles: ["yonetici", "operasyon", "goruntuleyici"], testid: "sidebar-nav-ayarlar-link" },
];

const PAGE_TITLES = {
  "/": "Dashboard",
  "/ciro": "Ciro",
  "/gider": "Gider & Bütçe",
  "/maliyet": "Maliyet (SMM)",
  "/nakit-akis": "Nakit Akış",
  "/finansal-analiz": "Finansal Analiz",
  "/personel": "Personel",
  "/ayarlar": "Ayarlar",
};

const SidebarContent = ({ user, onNavigate }) => {
  const visibleItems = NAV_ITEMS.filter((item) => item.roles.includes(user.role));

  return (
    <div className="flex h-full flex-col bg-[hsl(var(--primary))] text-white">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--accent))] font-display text-lg font-bold text-[hsl(var(--primary))]">
          3A
        </div>
        <div>
          <p className="font-display text-base font-semibold leading-tight">3A Finans</p>
          <p className="text-[11px] text-white/50">Finansal Yönetim</p>
        </div>
      </div>

      <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-white/40 font-display">
        Modüller
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            onClick={onNavigate}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${
                isActive
                  ? "bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(226,178,60,0.4)]"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0 opacity-90" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User bottom */}
      <div className="border-t border-white/10 px-4 py-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs font-semibold">
              {(user.ad_soyad || user.email).slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.ad_soyad || user.email}</p>
            <p className="text-[11px] text-white/50">{ROL_ETIKETLERI[user.role]}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppShell = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const pageTitle = PAGE_TITLES[location.pathname] || "3A Finans";

  return (
    <div className="min-h-screen bg-background font-body">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 z-40">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[280px] p-0 border-0">
          <SidebarContent user={user} onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-card px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden min-h-[44px] min-w-[44px]"
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Menüyü aç"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <h1 className="font-display text-lg font-semibold tracking-tight" data-testid="page-title">
            {pageTitle}
          </h1>

          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-2 min-h-[44px] px-2"
                  data-testid="topbar-user-menu-button"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[hsl(var(--primary))] text-white text-xs font-semibold">
                      {(user.ad_soyad || user.email).slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">
                    {user.ad_soyad || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium truncate">{user.email}</p>
                  <Badge variant="secondary" className="mt-1 text-[10px]" data-testid="topbar-user-role-badge">
                    {ROL_ETIKETLERI[user.role]}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="text-destructive focus:text-destructive cursor-pointer min-h-[44px]"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className="px-4 py-6 sm:px-6 lg:px-8 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
